import { z } from "zod";
import { DEFAULT_AI_CREDIT_POLICY, aiCreditPolicySchema } from "./ai-credits.ts";
import { DEFAULT_FAILED_PAYMENT_GRACE_PERIOD_DAYS, DEFAULT_SIGNUP_FREE_RUNS } from "./billing.ts";
import { llmPricingOverridesSchema } from "./llm-pricing.ts";

export const RUNTIME_FLAG_TYPES = ["boolean", "string", "number", "enum", "json"] as const;
export type RuntimeFlagType = (typeof RUNTIME_FLAG_TYPES)[number];

export const runtimeFlagTypeSchema = z.enum(RUNTIME_FLAG_TYPES);

export const RUNTIME_FLAG_SCOPES = ["global", "brand"] as const;
export type RuntimeFlagScope = (typeof RUNTIME_FLAG_SCOPES)[number];

export const runtimeFlagScopeSchema = z.enum(RUNTIME_FLAG_SCOPES);

export const runtimeFlagTargetSchema = z.discriminatedUnion("scope", [
  z.object({ scope: z.literal("global") }),
  z.object({ scope: z.literal("brand"), scopeId: z.uuid() }),
]);

export type RuntimeFlagTarget = z.infer<typeof runtimeFlagTargetSchema>;

export const runtimeFlagResolutionSourceSchema = z.enum(["default", ...RUNTIME_FLAG_SCOPES]);
export type RuntimeFlagResolutionSource = z.infer<typeof runtimeFlagResolutionSourceSchema>;

export const SOCIAL_POST_IMAGE_REALIZER_PROVIDERS = ["google", "openai"] as const;
export type SocialPostImageRealizerProvider = (typeof SOCIAL_POST_IMAGE_REALIZER_PROVIDERS)[number];
export const SOCIAL_POST_GOOGLE_IMAGE_REALIZER_MODEL = "gemini-3.1-flash-image-preview";

export const socialPostImageRealizerProviderSchema = z.enum(SOCIAL_POST_IMAGE_REALIZER_PROVIDERS);

export const SOCIAL_POST_IMAGE_QA_FAILURE_POLICIES = ["select_best", "fail_task"] as const;
export type SocialPostImageQaFailurePolicy = (typeof SOCIAL_POST_IMAGE_QA_FAILURE_POLICIES)[number];

export const socialPostImageQaFailurePolicySchema = z.enum(SOCIAL_POST_IMAGE_QA_FAILURE_POLICIES);

export const SOCIAL_AUTOPUBLISH_GLOBAL_MODES = ["brand_setting", "force_disabled"] as const;
export type SocialAutopublishGlobalMode = (typeof SOCIAL_AUTOPUBLISH_GLOBAL_MODES)[number];

export const socialAutopublishGlobalModeSchema = z.enum(SOCIAL_AUTOPUBLISH_GLOBAL_MODES);

export const BRAND_ONBOARDING_MODES = ["standard", "chatbot"] as const;
export type BrandOnboardingMode = (typeof BRAND_ONBOARDING_MODES)[number];

export const brandOnboardingModeSchema = z.enum(BRAND_ONBOARDING_MODES);
export const LEGACY_BRAND_ONBOARDING_MODE = "current";

export const ASSET_CREATION_FLOWS = ["template", "freestyle"] as const;
export type AssetCreationFlow = (typeof ASSET_CREATION_FLOWS)[number];

export const assetCreationFlowSchema = z.enum(ASSET_CREATION_FLOWS);
export const DEFAULT_ASSET_CREATION_FLOW: AssetCreationFlow = "template";

export const DEFAULT_BRAND_ASSISTANT_CHAT_CONTEXT_WINDOW_TOKENS = 1_000_000;

type CronFieldSpec = {
  min: number;
  max: number;
  aliases?: Record<string, number>;
};

const CRON_MONTH_ALIASES = {
  jan: 1,
  feb: 2,
  mar: 3,
  apr: 4,
  may: 5,
  jun: 6,
  jul: 7,
  aug: 8,
  sep: 9,
  oct: 10,
  nov: 11,
  dec: 12,
} satisfies Record<string, number>;

const CRON_WEEKDAY_ALIASES = {
  sun: 0,
  mon: 1,
  tue: 2,
  wed: 3,
  thu: 4,
  fri: 5,
  sat: 6,
} satisfies Record<string, number>;

const CRON_FIELD_SPECS_5 = [
  { min: 0, max: 59 },
  { min: 0, max: 23 },
  { min: 1, max: 31 },
  { min: 1, max: 12, aliases: CRON_MONTH_ALIASES },
  { min: 0, max: 7, aliases: CRON_WEEKDAY_ALIASES },
] as const satisfies readonly CronFieldSpec[];

const CRON_FIELD_SPECS_6 = [
  { min: 0, max: 59 },
  ...CRON_FIELD_SPECS_5,
] as const satisfies readonly CronFieldSpec[];

const CRON_NUMBER_PATTERN = /^\d+$/;

const normalizeCronFieldValue = (value: string, spec: CronFieldSpec): string => {
  const alias = spec.aliases?.[value.toLowerCase()];
  return alias === undefined ? value : String(alias);
};

const isCronNumberInRange = (value: string, spec: CronFieldSpec): boolean => {
  const normalized = normalizeCronFieldValue(value, spec);
  if (!CRON_NUMBER_PATTERN.test(normalized)) return false;

  const parsed = Number(normalized);
  return Number.isInteger(parsed) && parsed >= spec.min && parsed <= spec.max;
};

const isValidCronFieldBase = (base: string, spec: CronFieldSpec): boolean => {
  if (base === "*") return true;

  const rangeParts = base.split("-");
  if (rangeParts.length === 1) {
    return isCronNumberInRange(rangeParts[0] ?? "", spec);
  }

  if (rangeParts.length !== 2) return false;

  const [rawStart, rawEnd] = rangeParts;
  if (!rawStart || !rawEnd) return false;
  if (!isCronNumberInRange(rawStart, spec) || !isCronNumberInRange(rawEnd, spec)) return false;

  return (
    Number(normalizeCronFieldValue(rawStart, spec)) <= Number(normalizeCronFieldValue(rawEnd, spec))
  );
};

const isValidCronFieldPart = (part: string, spec: CronFieldSpec): boolean => {
  if (part.length === 0) return false;

  const stepParts = part.split("/");
  if (stepParts.length > 2) return false;

  const [base = "", step] = stepParts;
  if (!isValidCronFieldBase(base, spec)) return false;

  if (step === undefined) return true;
  if (!CRON_NUMBER_PATTERN.test(step)) return false;

  const parsedStep = Number(step);
  return Number.isInteger(parsedStep) && parsedStep >= 1 && parsedStep <= spec.max;
};

const isValidCronField = (field: string, spec: CronFieldSpec): boolean =>
  field.split(",").every((part) => isValidCronFieldPart(part, spec));

export const isValidCronExpression = (expression: string): boolean => {
  const fields = expression.trim().split(/\s+/).filter(Boolean);
  const specs =
    fields.length === 5 ? CRON_FIELD_SPECS_5 : fields.length === 6 ? CRON_FIELD_SPECS_6 : null;

  return (
    specs !== null &&
    fields.every((field, index) => {
      const spec = specs[index];
      return spec !== undefined && isValidCronField(field, spec);
    })
  );
};

export type RuntimeFlagScalarValue = boolean | string | number;

export type RuntimeFlagJsonNode =
  | string
  | number
  | boolean
  | null
  | { [key: string]: RuntimeFlagJsonNode }
  | RuntimeFlagJsonNode[];

export type RuntimeFlagJsonValue = { [key: string]: RuntimeFlagJsonNode } | RuntimeFlagJsonNode[];

const runtimeFlagJsonNodeSchema: z.ZodType<RuntimeFlagJsonNode> = z.lazy(() =>
  z.union([
    z.string(),
    z.number().finite(),
    z.boolean(),
    z.null(),
    z.record(z.string(), runtimeFlagJsonNodeSchema),
    z.array(runtimeFlagJsonNodeSchema),
  ]),
);

export const runtimeFlagJsonValueSchema: z.ZodType<RuntimeFlagJsonValue> = z.union([
  z.record(z.string(), runtimeFlagJsonNodeSchema),
  z.array(runtimeFlagJsonNodeSchema),
]);

export type RuntimeFlagValue = RuntimeFlagScalarValue | RuntimeFlagJsonValue;

type RuntimeFlagValueOfType<T extends RuntimeFlagType> = T extends "boolean"
  ? boolean
  : T extends "number"
    ? number
    : T extends "json"
      ? RuntimeFlagJsonValue
      : string;

type BaseRuntimeFlagDefinition<T extends RuntimeFlagType> = {
  name: string;
  type: T;
  allowedScopes: readonly RuntimeFlagScope[];
  defaultValue: RuntimeFlagValueOfType<T>;
  displayName: string;
  description?: string;
  validator?: (value: RuntimeFlagValueOfType<T>) => boolean;
};

type EnumRuntimeFlagDefinition<TValues extends readonly string[]> = Omit<
  BaseRuntimeFlagDefinition<"enum">,
  "defaultValue" | "validator"
> & {
  enumValues: TValues;
  defaultValue: TValues[number];
  validator?: (value: TValues[number]) => boolean;
};

type RuntimeFlagDefinition =
  | BaseRuntimeFlagDefinition<"boolean">
  | BaseRuntimeFlagDefinition<"string">
  | BaseRuntimeFlagDefinition<"number">
  | BaseRuntimeFlagDefinition<"json">
  | EnumRuntimeFlagDefinition<readonly string[]>;

const defineRuntimeFlags = <const T extends readonly RuntimeFlagDefinition[]>(flags: T): T => flags;

export const RUNTIME_FLAGS = defineRuntimeFlags([
  {
    name: "brand_kit_strategy_regeneration_debounce_minutes",
    type: "number",
    allowedScopes: ["global", "brand"],
    defaultValue: 30,
    validator: (value) => Number.isInteger(value) && value >= 1 && value <= 1_440,
    displayName: "Brand Kit Strategy Debounce Minutes",
    description: "Delay, in minutes, before queued brand kit edits trigger strategy regeneration.",
  },
  {
    name: "social_post_image_realizer_provider",
    type: "enum",
    allowedScopes: ["global", "brand"],
    enumValues: SOCIAL_POST_IMAGE_REALIZER_PROVIDERS,
    defaultValue: "google",
    displayName: "Social Post Image Realizer",
    description: "Controls which provider generates scene images for social posts.",
  },
  {
    name: "social_post_image_candidate_count",
    type: "number",
    allowedScopes: ["global", "brand"],
    defaultValue: 2,
    validator: (value) => Number.isInteger(value) && value >= 1 && value <= 4,
    displayName: "Social Post Image Candidate Count",
    description:
      "Number of scene image candidates to generate before selecting the rendered winner.",
  },
  {
    name: "social_post_image_qa_enabled",
    type: "boolean",
    allowedScopes: ["global", "brand"],
    defaultValue: true,
    displayName: "Social Post Image QA Enabled",
    description: "Runs vision QA over generated social post image candidates before selection.",
  },
  {
    name: "social_post_image_qa_retry_enabled",
    type: "boolean",
    allowedScopes: ["global", "brand"],
    defaultValue: true,
    displayName: "Social Post Image QA Retry Enabled",
    description:
      "Allows one targeted retry when all generated scene image candidates miss the QA threshold.",
  },
  {
    name: "social_post_image_qa_threshold_pct",
    type: "number",
    allowedScopes: ["global", "brand"],
    defaultValue: 50,
    validator: (value) => Number.isInteger(value) && value >= 0 && value <= 100,
    displayName: "Social Post Image QA Threshold",
    description:
      "Minimum QA score percentage used to classify generated image candidates as passing.",
  },
  {
    name: "social_post_image_qa_failure_policy",
    type: "enum",
    allowedScopes: ["global", "brand"],
    enumValues: SOCIAL_POST_IMAGE_QA_FAILURE_POLICIES,
    defaultValue: "select_best",
    displayName: "Social Post Image QA Failure Policy",
    description:
      "Controls whether failed image QA selects the best attempt or fails the generation task.",
  },
  {
    name: "social_post_planner_freeform_enabled",
    type: "boolean",
    allowedScopes: ["global", "brand"],
    defaultValue: false,
    displayName: "Single Asset Freestyle Planner Enabled",
    description:
      "Allows single social post assets to use freestyle pixel-coordinate planning instead of published layout templates.",
  },
  {
    name: "campaign_autopilot_enabled",
    type: "boolean",
    allowedScopes: ["global", "brand"],
    defaultValue: true,
    displayName: "Campaign Autopilot Enabled",
    description: "Controls whether approved brand strategies automatically generate campaigns.",
  },
  {
    name: "brand_onboarding_mode",
    type: "enum",
    allowedScopes: ["global"],
    enumValues: BRAND_ONBOARDING_MODES,
    defaultValue: "standard",
    displayName: "Brand Onboarding Mode",
    description: "Controls whether new brand onboarding uses the standard flow or chatbot flow.",
  },
  {
    name: "billing_enabled",
    type: "boolean",
    allowedScopes: ["global"],
    defaultValue: false,
    displayName: "Billing Enabled",
    description: "Controls whether the customer billing UI is visible in brand settings.",
  },
  {
    name: "billing_failed_payment_grace_period_days",
    type: "number",
    allowedScopes: ["global"],
    defaultValue: DEFAULT_FAILED_PAYMENT_GRACE_PERIOD_DAYS,
    validator: (value) => Number.isInteger(value) && value >= 0 && value <= 30,
    displayName: "Failed Payment Grace Period Days",
    description:
      "Number of days a past-due Stripe subscription keeps paid entitlements after payment failure.",
  },
  {
    name: "signup_free_runs",
    type: "number",
    allowedScopes: ["global"],
    defaultValue: DEFAULT_SIGNUP_FREE_RUNS,
    validator: (value) => Number.isInteger(value) && value >= 0 && value <= 10_000,
    displayName: "Signup Free Runs",
    description: "One-time runs granted to a workspace when its owner signs up.",
  },
  {
    name: "ai_credit_policy",
    type: "json",
    allowedScopes: ["global"],
    defaultValue: DEFAULT_AI_CREDIT_POLICY,
    validator: (value) => aiCreditPolicySchema.safeParse(value).success,
    displayName: "AI Run Policy",
    description: "Controls queue-time run deductions and customer run pricing.",
  },
  {
    name: "real_people_detection_confidence_threshold_pct",
    type: "number",
    allowedScopes: ["global"],
    defaultValue: 80,
    validator: (value) => Number.isInteger(value) && value >= 0 && value <= 100,
    displayName: "Real People Detection Confidence Threshold",
    description:
      "Minimum classifier confidence percentage required to treat an asset as containing clearly visible real people.",
  },
  {
    name: "social_publish_instagram_enabled",
    type: "boolean",
    allowedScopes: ["global"],
    defaultValue: false,
    displayName: "Instagram Publishing Enabled",
    description: "Allows worker jobs to publish posts to Instagram instead of failing safely.",
  },
  {
    name: "social_autopublish_global_mode",
    type: "enum",
    allowedScopes: ["global"],
    enumValues: SOCIAL_AUTOPUBLISH_GLOBAL_MODES,
    defaultValue: "brand_setting",
    displayName: "Social Autopublish Global Mode",
    description:
      "Controls whether automated social publishing follows brand settings or is disabled globally.",
  },
  {
    name: "social_autopublish_enabled",
    type: "boolean",
    allowedScopes: ["brand"],
    defaultValue: false,
    displayName: "Social Autopublish Enabled",
    description: "Allows approved scheduled social posts for this brand to publish automatically.",
  },
  {
    name: "pricing_sync_enabled",
    type: "boolean",
    allowedScopes: ["global"],
    defaultValue: true,
    displayName: "Pricing Sync Enabled",
    description: "Enables scheduled synchronization of model pricing metadata.",
  },
  {
    name: "pricing_sync_cron",
    type: "string",
    allowedScopes: ["global"],
    defaultValue: "0 */6 * * *",
    validator: isValidCronExpression,
    displayName: "Pricing Sync Cron",
    description: "Cron expression, in UTC, for scheduled model pricing synchronization.",
  },
  {
    name: "models_dev_api_url",
    type: "string",
    allowedScopes: ["global"],
    defaultValue: "https://models.dev/api.json",
    validator: (value) => z.url().safeParse(value).success,
    displayName: "Models.dev API URL",
    description: "URL used to fetch public model pricing metadata.",
  },
  {
    name: "models_dev_provider_allow_list",
    type: "string",
    allowedScopes: ["global"],
    defaultValue: "openai,google",
    validator: (value) => value.split(",").some((item) => item.trim().length > 0),
    displayName: "Models.dev Provider Allow List",
    description: "Comma-separated provider ids imported from models.dev pricing metadata.",
  },
  {
    name: "llm_pricing_overrides",
    type: "json",
    allowedScopes: ["global"],
    defaultValue: [],
    validator: (value) => llmPricingOverridesSchema.safeParse(value).success,
    displayName: "LLM Pricing Overrides",
    description:
      "Structured price overrides used when imported model pricing is missing or incorrect.",
  },
  {
    name: "llm_materialized_prompt_sampling_rate",
    type: "number",
    allowedScopes: ["global"],
    defaultValue: 1,
    validator: (value) => value >= 0 && value <= 1,
    displayName: "LLM Materialized Prompt Sampling Rate",
    description:
      "Fraction of LLM calls whose materialized prompt input is stored for prompt quality review.",
  },
  {
    name: "openai_chat_model",
    type: "string",
    allowedScopes: ["global"],
    defaultValue: "gpt-4.1-mini",
    validator: (value) => value.trim().length > 0,
    displayName: "OpenAI Chat Model",
    description: "Model used by API chat and strategy reasoning calls.",
  },
  {
    name: "brand_assistant_chat_context_window_tokens",
    type: "number",
    allowedScopes: ["global"],
    defaultValue: DEFAULT_BRAND_ASSISTANT_CHAT_CONTEXT_WINDOW_TOKENS,
    validator: (value) => Number.isInteger(value) && value >= 1_000 && value <= 1_000_000,
    displayName: "Brand Assistant Chat Context Window Tokens",
    description:
      "Conventional total model context window used to derive Brand Assistant chat compaction budgets. This does not introspect provider model limits.",
  },
  {
    name: "worker_openai_model",
    type: "string",
    allowedScopes: ["global"],
    defaultValue: "gpt-4.1-mini",
    validator: (value) => value.trim().length > 0,
    displayName: "Worker OpenAI Model",
    description: "Default OpenAI model used by worker generation and analysis jobs.",
  },
  {
    name: "worker_analysis_model",
    type: "string",
    allowedScopes: ["global"],
    defaultValue: "gpt-4.1-mini",
    validator: (value) => value.trim().length > 0,
    displayName: "Worker Analysis Model",
    description:
      "OpenAI model used by worker ingestion, fact extraction, and visual analysis jobs.",
  },
  {
    name: "worker_generation_model",
    type: "string",
    allowedScopes: ["global"],
    defaultValue: "gpt-4.1-mini",
    validator: (value) => value.trim().length > 0,
    displayName: "Worker Generation Model",
    description: "OpenAI model used by worker strategy, campaign, and visual universe jobs.",
  },
  {
    name: "worker_openai_image_generation_model",
    type: "string",
    allowedScopes: ["global"],
    defaultValue: "gpt-image-2",
    validator: (value) => value.trim().length > 0,
    displayName: "Worker OpenAI Image Generation Model",
    description: "OpenAI image model used by worker image realization and regeneration jobs.",
  },
  {
    name: "worker_social_post_image_qa_model",
    type: "string",
    allowedScopes: ["global"],
    defaultValue: "gpt-4.1-mini",
    validator: (value) => value.trim().length > 0,
    displayName: "Worker Social Post Image QA Model",
    description: "OpenAI model used by the worker social post generated image QA step.",
  },
  {
    name: "worker_social_post_planner_model",
    type: "string",
    allowedScopes: ["global"],
    defaultValue: "gpt-5.2",
    validator: (value) => value.trim().length > 0,
    displayName: "Worker Social Post Planner Model",
    description: "OpenAI model used by the worker social post planning step.",
  },
  {
    name: "worker_social_post_copywriter_model",
    type: "string",
    allowedScopes: ["global"],
    defaultValue: "gpt-5.2",
    validator: (value) => value.trim().length > 0,
    displayName: "Worker Social Post Copywriter Model",
    description: "OpenAI model used by the worker social post copywriting step.",
  },
] as const);

export type RuntimeFlagDefinitionRecord = (typeof RUNTIME_FLAGS)[number];
export type RuntimeFlagName = RuntimeFlagDefinitionRecord["name"];

export const ASSET_CREATION_FLOW_RUNTIME_FLAG =
  "social_post_planner_freeform_enabled" satisfies RuntimeFlagName;

export type RuntimeFlagDefinitionByName<TName extends RuntimeFlagName> = Extract<
  RuntimeFlagDefinitionRecord,
  { name: TName }
>;

export type RuntimeFlagValueByName<TName extends RuntimeFlagName> =
  RuntimeFlagDefinitionByName<TName> extends {
    enumValues: readonly (infer TValue extends string)[];
  }
    ? TValue
    : RuntimeFlagDefinitionByName<TName> extends { type: infer TType extends RuntimeFlagType }
      ? RuntimeFlagValueOfType<TType>
      : never;

export type RuntimeFlagDefaults = Partial<{
  [TName in RuntimeFlagName]: RuntimeFlagValueByName<TName>;
}>;

export const runtimeFlagValueSchema = z.union([
  z.boolean(),
  z.string(),
  z.number().finite(),
  runtimeFlagJsonValueSchema,
]);

export const runtimeFlagNameSchema = z.enum(RUNTIME_FLAGS.map((flag) => flag.name));

export const runtimeFlagDefinitionSchema = z.object({
  name: runtimeFlagNameSchema,
  type: runtimeFlagTypeSchema,
  allowedScopes: z.array(runtimeFlagScopeSchema).readonly(),
  displayName: z.string(),
  description: z.string().optional(),
  defaultValue: runtimeFlagValueSchema,
  enumValues: z.array(z.string()).readonly().optional(),
});

export const materializedRuntimeFlagSchema = runtimeFlagDefinitionSchema.extend({
  value: runtimeFlagValueSchema,
  isOverridden: z.boolean(),
  source: runtimeFlagResolutionSourceSchema,
});

export type MaterializedRuntimeFlag = z.infer<typeof materializedRuntimeFlagSchema>;

export const validateRuntimeFlagValueForDefinition = (
  definition: RuntimeFlagDefinition,
  value: unknown,
): boolean => {
  if (definition.type === "boolean") {
    return typeof value === "boolean";
  }

  if (definition.type === "number") {
    return (
      typeof value === "number" && Number.isFinite(value) && (definition.validator?.(value) ?? true)
    );
  }

  if (definition.type === "string") {
    return typeof value === "string" && (definition.validator ? definition.validator(value) : true);
  }

  if (definition.type === "json") {
    return (
      runtimeFlagJsonValueSchema.safeParse(value).success &&
      (definition.validator ? definition.validator(value as RuntimeFlagJsonValue) : true)
    );
  }

  if (typeof value !== "string" || !definition.enumValues.includes(value)) {
    return false;
  }

  return definition.validator ? definition.validator(value) : true;
};

export const getRuntimeFlagDefinition = <TName extends RuntimeFlagName>(
  name: TName,
): RuntimeFlagDefinitionByName<TName> => {
  const flag = RUNTIME_FLAGS.find(
    (item): item is RuntimeFlagDefinitionByName<TName> => item.name === name,
  );

  if (!flag) {
    throw new Error(`Runtime flag not found: ${name}`);
  }

  return flag;
};

export const getRuntimeFlagDefinitions = (
  defaults: RuntimeFlagDefaults = {},
): RuntimeFlagDefinitionRecord[] =>
  RUNTIME_FLAGS.map((flag) => ({
    ...flag,
    defaultValue: defaults[flag.name] ?? flag.defaultValue,
  })) as RuntimeFlagDefinitionRecord[];

export const validateRuntimeFlagValue = <TName extends RuntimeFlagName>(
  name: TName,
  value: unknown,
): value is RuntimeFlagValueByName<TName> => {
  return validateRuntimeFlagValueForDefinition(getRuntimeFlagDefinition(name), value);
};

export const normalizeRuntimeFlagValue = <TName extends RuntimeFlagName>(
  name: TName,
  value: unknown,
): unknown => {
  if (name === "brand_onboarding_mode" && value === LEGACY_BRAND_ONBOARDING_MODE) {
    return "standard" satisfies BrandOnboardingMode;
  }

  if (name === "ai_credit_policy") {
    const parsed = aiCreditPolicySchema.safeParse(value);
    return parsed.success ? parsed.data : value;
  }

  return value;
};

export const validateRuntimeFlagScope = (
  definition: RuntimeFlagDefinition,
  scope: RuntimeFlagScope,
): boolean => definition.allowedScopes.includes(scope);

export const resolveSocialAutopublishEnabled = (input: {
  globalMode: SocialAutopublishGlobalMode;
  brandEnabled: boolean;
}): boolean => {
  if (input.globalMode === "force_disabled") return false;
  return input.brandEnabled;
};

export const assetCreationFlowFromFreeformEnabled = (freeformEnabled: boolean): AssetCreationFlow =>
  freeformEnabled ? "freestyle" : "template";

export const freeformEnabledFromAssetCreationFlow = (flow: AssetCreationFlow): boolean =>
  flow === "freestyle";
